'use client'

import { useTranslations } from 'next-intl'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  FormInput,
  FormMessage,
  FormSection,
  FormToggleRow,
  ORDER_FORM_FIELD_CLASS,
} from '@/features/order/order-form/components/widget/order-form-fields'
import { AutoApproveKind, RfqAutoApproveDraft, RfqAutoRetryDraft } from '@/features/rfq/types'
import { useNumberInput } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'

const APPROVE_KINDS: AutoApproveKind[] = ['markOffsetPercent', 'markOffsetValue', 'limitPrice']

const APPROVE_KIND_LABEL_KEYS: Record<AutoApproveKind, string> = {
  markOffsetPercent: 'rfqApproveKindMarkPercent',
  markOffsetValue: 'rfqApproveKindMarkValue',
  limitPrice: 'rfqApproveKindLimitPrice',
}

const APPROVE_KIND_INFO_KEYS: Record<AutoApproveKind, string> = {
  limitPrice: 'rfqLimitPriceInfoBuy',
  markOffsetPercent: 'rfqMarkOffsetPercentInfo',
  markOffsetValue: 'rfqMarkOffsetValueInfo',
}

interface RfqAutoFieldsProps {
  autoApprove: RfqAutoApproveDraft
  autoRetry: RfqAutoRetryDraft
  errorByPath: Record<string, string>
  showErrors: boolean
  isAllSell: boolean
  onApproveChange: (next: RfqAutoApproveDraft) => void
  onRetryChange: (next: RfqAutoRetryDraft) => void
}

export function RfqAutoFields({
  autoApprove,
  autoRetry,
  errorByPath,
  showErrors,
  isAllSell,
  onApproveChange,
  onRetryChange,
}: RfqAutoFieldsProps) {
  const t = useTranslations('orderForm')
  // A package limit price can be a net credit; the mark-offset bands are magnitudes.
  const thresholdInput = useNumberInput(autoApprove.value, {
    allowNegative: autoApprove.kind === 'limitPrice',
  })
  // Suppress the missing-threshold error while the field is still pristine (empty);
  // submit re-validates and surfaces it as a toast, so the form doesn't open with a warning.
  const thresholdError =
    showErrors && autoApprove.value.trim() !== '' ? errorByPath['autoApprove.value'] : undefined
  const retryError = showErrors ? errorByPath['autoRetry'] : undefined
  const approveKindInfo =
    autoApprove.kind === 'limitPrice' && isAllSell
      ? t('rfqLimitPriceInfoSell')
      : t(APPROVE_KIND_INFO_KEYS[autoApprove.kind])

  return (
    // Unboxed, directly under the Manual/Auto switch it belongs to: a nested bordered panel
    // inside the widget would read as a separate card.
    <div className="flex flex-col gap-4">
      <FormSection
        label={t('rfqAutoApproveWhenLabel')}
        info={`${approveKindInfo}\n\n${t('rfqAutoApproveBaseInfo')}`}
        error={thresholdError}
        hint={thresholdError ? undefined : t('rfqAutoApproveHint')}
      >
        <div className="flex items-center gap-2">
          <Select
            value={autoApprove.kind}
            onValueChange={(kind) =>
              onApproveChange({ ...autoApprove, kind: kind as AutoApproveKind })
            }
          >
            <SelectTrigger className={cn(ORDER_FORM_FIELD_CLASS, 'min-w-0 flex-1')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {APPROVE_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {t(APPROVE_KIND_LABEL_KEYS[kind])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormInput
            containerClassName="w-[120px]"
            aria-label={t('rfqValuePlaceholder')}
            placeholder={t('rfqValuePlaceholder')}
            {...thresholdInput.bind((value) => onApproveChange({ ...autoApprove, value }))}
          />
        </div>
      </FormSection>

      <FormToggleRow
        label={t('rfqAutoRetryOnExpiryLabel')}
        info={t('rfqAutoRetryInfo')}
        checked={autoRetry.enabled}
        onCheckedChange={(enabled) => onRetryChange({ ...autoRetry, enabled })}
      >
        <div className="flex items-center gap-2">
          <FormInput
            containerClassName="flex-1"
            label={t('rfqMaxRetriesPlaceholder')}
            inputMode="numeric"
            value={autoRetry.maxRetries}
            onChange={(e) => onRetryChange({ ...autoRetry, maxRetries: e.target.value })}
          />
          <FormInput
            containerClassName="flex-1"
            label={t('rfqRetryUntilPlaceholder')}
            inputMode="numeric"
            value={autoRetry.retryUntilMs}
            onChange={(e) => onRetryChange({ ...autoRetry, retryUntilMs: e.target.value })}
          />
        </div>
        <FormMessage error={retryError} hint={t('rfqRetryUntilHint')} />
      </FormToggleRow>
    </div>
  )
}
